/**
 * App Store — Phase 4 storefront analytics + paid acquisition.
 *
 * The top of the funnel (App Store Connect: impressions → product-page views →
 * installs) and the cost side (Apple Search Ads spend → CAC ladder). Everything
 * is empty until the Phase-4 ingesters run against a live app + real ad spend;
 * empty states say what activates each panel, never read as broken.
 */
import { Bars, Chip, Empty, ErrorNote, Panel, Readout, useApi } from "../components/ui.js";
import { fmtDate, fmtMonthDay, int, n, pct, usd } from "../lib/format.js";

interface FunnelRow {
  day: string; source_type: string; impressions: unknown; product_page_views: unknown;
  installs: unknown; reinstalls: unknown; proceeds_usd: unknown;
  impression_to_page_rate: unknown; page_to_install_rate: unknown;
}
/** Server-computed headline totals (see summarizeAppStoreFunnel in the API). */
interface FunnelSummary { impressions: number; pageViews: number; installs: number }
interface SpendRow { channel: string; campaign: string | null; day: string; spend_usd: unknown; impressions: unknown; taps: unknown; installs: unknown; }
interface CacRow {
  channel: string; campaign: string | null; spend_usd: unknown; ad_impressions: unknown; taps: unknown;
  provider_installs: unknown; attributed_installs: unknown; activated_48h: unknown; subscriptions: unknown;
  mrr_usd: unknown; cost_per_install: unknown; cost_per_activated: unknown; cost_per_paid: unknown; mrr_per_dollar: unknown;
}
interface CampaignRow { id: string; provider: string; name: string; channel: string; status: string; daily_budget_usd: unknown; currency: string; started_at: string | null; synced_at: string | null; }
interface UsageRow { kind: string; events: unknown; installs: unknown; last_seen: string | null; }
interface UsageSummary { totalEvents: number; activeInstalls: number; purchases: number; }
interface CampaignInstallRow {
  channel: unknown; campaign: unknown; matched_installs: unknown; within_2h: unknown;
  subscriptions: unknown; mrr_usd: unknown; avg_click_to_install_min: unknown;
}
interface CampaignSummary { campaigns: number; matchedInstalls: number; within2h: number; subscriptions: number; mrrUsd: number; }

const money = (v: unknown) => (v == null ? "—" : usd(v));

export function AppStore() {
  const data = useApi<{ funnel: FunnelRow[]; funnelSummary: FunnelSummary; spendByDay: SpendRow[]; cac: CacRow[]; campaigns: CampaignRow[]; appUsage: UsageRow[]; appUsageSummary: UsageSummary; campaignInstalls: CampaignInstallRow[]; campaignSummary: CampaignSummary }>("/api/acquisition");

  const funnel = data.data?.funnel ?? [];
  const spendByDay = data.data?.spendByDay ?? [];
  const cac = data.data?.cac ?? [];
  const campaigns = data.data?.campaigns ?? [];
  const appUsage = data.data?.appUsage ?? [];
  const usage = data.data?.appUsageSummary ?? { totalEvents: 0, activeInstalls: 0, purchases: 0 };
  const campaignInstalls = data.data?.campaignInstalls ?? [];
  const campaignSummary = data.data?.campaignSummary ?? { campaigns: 0, matchedInstalls: 0, within2h: 0, subscriptions: 0, mrrUsd: 0 };

  // Headline storefront totals are computed SERVER-SIDE (summarizeAppStoreFunnel):
  // impressions/page-views come from the per-source Analytics-Reports rows and
  // downloads from the authoritative Sales ('all') rows. Summing the wrong set on
  // the client was the old bug that pinned impressions to 0.
  const summary = data.data?.funnelSummary ?? { impressions: 0, pageViews: 0, installs: 0 };
  const impressions = summary.impressions;
  const pageViews = summary.pageViews;
  const installs = summary.installs;
  const totalSpend = spendByDay.reduce((s, r) => s + n(r.spend_usd), 0);

  // downloads-by-day bars come from the authoritative Sales ('all') rows.
  const byDay = funnel.filter((r) => r.source_type === "all").slice().reverse();

  return (
    <>
      <div className="page-head">
        <h1>App Store</h1>
        <span className="stamp">storefront + in-app usage + paid acquisition</span>
      </div>

      {data.error ? <ErrorNote error={data.error} /> : null}

      <div className="grid cols-3" style={{ marginBottom: 14 }}>
        <Panel title="Impressions" delay={0}>
          <Readout hero label="app store, trailing" value={int(impressions)}
                   note={impressions === 0 ? "no App Store analytics yet" : undefined} tone={impressions === 0 ? "dim" : undefined} />
        </Panel>
        <Panel title="Product page views" delay={60}>
          <Readout hero label="from impressions" value={int(pageViews)}
                   note={impressions > 0 ? `${pct(impressions ? pageViews / impressions : 0)} of impressions` : undefined} tone={pageViews === 0 ? "dim" : undefined} />
        </Panel>
        <Panel title="Downloads" delay={120}>
          <Readout hero label="app store units" value={int(installs)} tone={installs === 0 ? "dim" : "green"}
                   note={pageViews > 0 ? `${pct(pageViews ? installs / pageViews : 0)} page→install` : undefined} />
        </Panel>
      </div>
      <div className="tag" style={{ marginBottom: 14 }}>
        “Downloads” = Apple Sales report (app units). “Impressions/page views” = Apple Analytics-Reports
        (by source). Distinct from the funnel’s <strong>attributed installs</strong> (campaign-matched) and the
        pipeline’s <strong>installed contacts</strong> (identified people) — three different denominators.
      </div>

      <Panel title="App Store funnel — impression → product page → install" delay={160}>
        {funnel.length === 0 ? (
          <Empty>
            no App Store Connect analytics yet — needs the app live on the App Store + an ASC API key with Analytics/Sales access.
            The Phase-4 ingester writes this daily once both exist.
          </Empty>
        ) : (
          <>
            <Bars values={byDay.map((r) => n(r.installs))} labels={byDay.map((r) => fmtMonthDay(r.day))} color="var(--green)" />
            <hr className="rule" />
            <table className="data">
              <thead><tr><th>source</th><th>impressions</th><th>page views</th><th>installs</th><th>impr→page</th><th>page→install</th></tr></thead>
              <tbody>
                {Object.values(funnel.reduce((acc: Record<string, FunnelRow & { _src: string }>, r) => {
                  const k = r.source_type;
                  const a = acc[k] ?? { ...r, _src: k, impressions: 0, product_page_views: 0, installs: 0 };
                  a.impressions = n(a.impressions) + n(r.impressions);
                  a.product_page_views = n(a.product_page_views) + n(r.product_page_views);
                  a.installs = n(a.installs) + n(r.installs);
                  acc[k] = a; return acc;
                }, {})).map((r) => (
                  <tr key={r._src}>
                    <td className="mono">{r._src}</td>
                    <td className="num">{int(r.impressions)}</td>
                    <td className="num">{int(r.product_page_views)}</td>
                    <td className="num">{int(r.installs)}</td>
                    <td className="num dim">{pct(n(r.impressions) ? n(r.product_page_views) / n(r.impressions) : 0)}</td>
                    <td className="num dim">{pct(n(r.product_page_views) ? n(r.installs) / n(r.product_page_views) : 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </Panel>

      <Panel title="In-app usage — what people do after install" delay={180}>
        {appUsage.length === 0 ? (
          <Empty>
            no in-app events yet — FunnelBeacon posts session / report / purchase events to
            /ingress/app-event once people use the app.
          </Empty>
        ) : (
          <>
            <div className="grid cols-3" style={{ marginBottom: 12 }}>
              <Readout hero label="usage events" value={int(usage.totalEvents)} />
              <Readout hero label="installs w/ activity" value={int(usage.activeInstalls)} />
              <Readout hero label="purchases" value={int(usage.purchases)} tone={usage.purchases > 0 ? "green" : "dim"} />
            </div>
            <Bars values={appUsage.map((r) => n(r.events))} labels={appUsage.map((r) => r.kind)} color="var(--blue)" />
            <hr className="rule" />
            <table className="data">
              <thead><tr><th>event</th><th>count</th><th>installs</th><th>last seen</th></tr></thead>
              <tbody>
                {appUsage.map((r) => (
                  <tr key={r.kind}>
                    <td className="mono">{r.kind}</td>
                    <td className="num">{int(r.events)}</td>
                    <td className="num dim">{int(r.installs)}</td>
                    <td className="num dim">{fmtDate(r.last_seen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tag" style={{ marginTop: 8 }}>
              anonymous by install (app_account_token) until a purchase or captured email links it to a contact
            </div>
          </>
        )}
      </Panel>

      <Panel title="Campaign → install — first-party time-match (directional)" delay={190}>
        {campaignInstalls.length === 0 ? (
          <Empty>
            no matched installs yet — each install is attributed to the most recent /go click in the 24h before it.
            Fills in as campaign clicks and installs accrue. Apple’s own ct-campaign view counts live in App Store
            Connect → Acquisition (UI only) — not available via the analytics API.
          </Empty>
        ) : (
          <>
            <div className="grid cols-4" style={{ marginBottom: 12 }}>
              <Readout hero label="matched installs" value={int(campaignSummary.matchedInstalls)} tone={campaignSummary.matchedInstalls > 0 ? "green" : "dim"} />
              <Readout hero label="within 2h · higher conf." value={int(campaignSummary.within2h)} />
              <Readout hero label="→ subscriptions" value={int(campaignSummary.subscriptions)} tone={campaignSummary.subscriptions > 0 ? "green" : "dim"} />
              <Readout hero label="attributed MRR" value={usd(campaignSummary.mrrUsd)} />
            </div>
            <table className="data">
              <thead><tr><th>channel</th><th>campaign</th><th>matched installs</th><th>within 2h</th><th>subs</th><th>mrr</th><th>avg click→install</th></tr></thead>
              <tbody>
                {campaignInstalls.map((r, i) => (
                  <tr key={i}>
                    <td><Chip tone="blue">{String(r.channel ?? "—")}</Chip></td>
                    <td className="dim">{r.campaign ? String(r.campaign) : "—"}</td>
                    <td className="num">{int(r.matched_installs)}</td>
                    <td className="num dim">{int(r.within_2h)}</td>
                    <td className="num">{int(r.subscriptions)}</td>
                    <td className="num">{usd(r.mrr_usd)}</td>
                    <td className="num dim">{r.avg_click_to_install_min == null ? "—" : `${int(r.avg_click_to_install_min)} min`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="tag" style={{ marginTop: 8 }}>
              heuristic: last-touch time-match — a click is not a guaranteed install, and coincidental matches rise with
              click volume; treat as directional. “within 2h” is the higher-confidence subset.
            </div>
          </>
        )}
      </Panel>

      <div className="grid cols-2" style={{ marginTop: 14 }}>
        <Panel title="Paid spend · trailing" delay={200}>
          {spendByDay.length === 0 ? (
            <Empty>no paid spend yet — connect an Apple Search Ads account and run a campaign. Spend + CAC fill in here.</Empty>
          ) : (
            <>
              <Readout hero label="total spend" value={usd(totalSpend)} />
              <hr className="rule" />
              <Bars values={spendByDay.slice().reverse().map((r) => n(r.spend_usd))} labels={spendByDay.slice().reverse().map((r) => fmtMonthDay(r.day))} color="var(--amber)" />
            </>
          )}
        </Panel>

        <Panel title="Campaigns" delay={240}>
          {campaigns.length === 0 ? (
            <Empty>no campaigns — synced from the Apple Search Ads API once a campaign is live</Empty>
          ) : (
            <table className="data">
              <thead><tr><th>campaign</th><th>channel</th><th>status</th><th>daily cap</th></tr></thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c.id}>
                    <td>{c.name}<div className="faint" style={{ fontSize: 12 }}>{c.provider}</div></td>
                    <td className="dim">{c.channel}</td>
                    <td><Chip tone={c.status === "active" ? "green" : "amber"}>{c.status}</Chip></td>
                    <td className="num dim">{money(c.daily_budget_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      </div>

      <Panel title="CAC ladder — cost per install → activated → paid (optimize on activated, not raw installs)" delay={280}>
        {cac.length === 0 ? (
          <Empty>
            no CAC yet — this joins paid spend to attributed installs/activations/subscriptions.
            Fills in once there is both ad spend and attributed conversions.
          </Empty>
        ) : (
          <table className="data">
            <thead><tr>
              <th>channel</th><th>campaign</th><th>spend</th><th>attr. installs</th><th>activated</th><th>paid</th>
              <th>cost/install</th><th>cost/activated</th><th>cost/paid</th><th>mrr/$</th>
            </tr></thead>
            <tbody>
              {cac.map((r, i) => (
                <tr key={i}>
                  <td><Chip tone="blue">{r.channel}</Chip></td>
                  <td className="dim">{r.campaign ?? "—"}</td>
                  <td className="num">{usd(r.spend_usd)}</td>
                  <td className="num">{int(r.attributed_installs)}</td>
                  <td className="num">{int(r.activated_48h)}</td>
                  <td className="num">{int(r.subscriptions)}</td>
                  <td className="num">{money(r.cost_per_install)}</td>
                  <td className="num" style={{ fontWeight: 600 }}>{money(r.cost_per_activated)}</td>
                  <td className="num">{money(r.cost_per_paid)}</td>
                  <td className="num dim">{r.mrr_per_dollar == null ? "—" : `${n(r.mrr_per_dollar).toFixed(2)}×`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="tag" style={{ marginTop: 8 }}>
          budget guardrail: optimize cost-per-<strong>activated-qualified</strong> user → retained → paid, never cheapest install
        </div>
      </Panel>

      {campaigns.some((c) => c.synced_at) ? (
        <div className="tag" style={{ marginTop: 10 }}>
          last campaign sync: {fmtDate(campaigns.find((c) => c.synced_at)?.synced_at ?? null)}
        </div>
      ) : null}
    </>
  );
}
